import { useLocation, useNavigate } from 'react-router';
import { routes } from '@/constants/Constants';
import { useSplitFeatureFlag } from '@/hooks/useSplitFeatureFlag';
import { SplitFeatureFlag } from '@/utils/split/splitConfig';
import { usePermission } from '@/hooks/usePermission';
import { UserPermissions } from '@/constants/permissions.constant';
import useRenterInviteDrawerStore from '@/stores/useRenterInviteDrawerStore';
import { RENTER_INVITE_QUERY_PARAM } from '../constants';
import { InviteEntryPoint } from '../RenterInvite.types';

/**
 * FF ON + both Invite & App Tracker permissions:
 *   - If already on App Tracker → open drawer in-place
 *   - Otherwise → navigate to App Tracker with ?invite=open (auto-opens drawer)
 *
 * FF ON + Invite only (no App Tracker):
 *   - Open drawer on current page
 *
 * FF OFF:
 *   - Navigate to /renter-invite (existing full-page behavior)
 */
export const useRenterInviteAction = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isFeatureFlagEnabled } = useSplitFeatureFlag(
    SplitFeatureFlag.MONARCH_460_NEW_RENTER_INVITE_IN_DRAWER,
  );
  const { openDrawer } = useRenterInviteDrawerStore();

  const { notSet: appTrackerNotSet, enabled: appTrackerEnabled } = usePermission(
    UserPermissions.Deal.CanAccessDealsDashboard,
  );
  const canAccessAppTracker = appTrackerNotSet || appTrackerEnabled;

  const isOnAppTracker = location.pathname === routes.dealsDashboard;

  const resolveEntryPoint = (): InviteEntryPoint => {
    if (isOnAppTracker) return 'app_tracker';
    return 'll_portal_homepage';
  };

  const openInvite = () => {
    if (!isFeatureFlagEnabled) {
      navigate(routes.renterInvite);
      return;
    }

    if (canAccessAppTracker) {
      if (isOnAppTracker) {
        openDrawer(resolveEntryPoint());
      } else {
        navigate(`${routes.dealsDashboard}?${RENTER_INVITE_QUERY_PARAM}=true`, {
          state: { inviteEntryPoint: resolveEntryPoint() },
        });
      }
    } else {
      openDrawer(resolveEntryPoint());
    }
  };

  return { openInvite, isDrawerMode: isFeatureFlagEnabled };
};
